import "pixi.js/advanced-blend-modes";
import { useEffect, useRef } from "react";
import { Application, BlurFilter, Container, Sprite } from "pixi.js";
import { closeupPerson, CH, CW, designCloseup, finaleCloseup, launchCloseup, lightMaps, look, playtestCloseup, playtestTable, type Bitmap, type BuildingLevel, type CloseupScene, type Look, type Mood } from "@bd/art";
import { Dust } from "./dust";
import { HD2DFilter } from "./post";
import { freshTex, texLazy } from "./textures";

export interface Actor {
  id: string;
  look: Look;
  seat: number; // index into scene.seats, or -1 for the designer spot
  mouth: "smile" | "grin" | "flat" | "open";
  armsUp: boolean;
  lean: number; // pixels leaning back
  /** stand up (the launch crowd); by default only the designer spot stands */
  stand?: boolean;
}

export interface CloseupHandle {
  scene: CloseupScene;
  /** Screen position (CSS px) of a world point in the close-up. */
  toScreen: (x: number, y: number) => { x: number; y: number };
}

/** A front-on close-up room, lit like a stage. Renders actors at seats; the caller drives their expressions. */
/** Playtest only: the game on the table (its mechanic, theme and format; the name seeds where the pieces lie). */
export interface CloseupTable {
  mechanic: string;
  theme: string;
  format: string;
  name: string;
}

export function CloseupCanvas({ kind, year, actors, mood = "dusk", quality, onReady, zoomFrom, display, level, table }: {
  kind: "playtest" | "design" | "launch" | "finale";
  year: number;
  actors: Actor[];
  mood?: Mood;
  quality: "high" | "low";
  onReady?: (h: CloseupHandle) => void;
  zoomFrom?: { x: number; y: number } | null;
  /** launch day: the new box, stood on the scene's plinth */
  display?: Bitmap;
  /** playtest: the company's building, which sets the room (kitchen table, meeting room, lab, HQ lab) */
  level?: BuildingLevel;
  /** playtest: what is on the table; without it the table shows a plain board */
  table?: CloseupTable;
}) {
  const host = useRef<HTMLDivElement>(null);
  const state = useRef<{ app: Application; layers: { far: Container; near: Container }; scene: CloseupScene; scale: number; ox: number; oy: number } | null>(null);
  const actorsRef = useRef(actors);
  actorsRef.current = actors;

  useEffect(() => {
    let disposed = false;
    let onResize = () => {};
    const app = new Application();
    void app.init({ preference: "webgl", antialias: false, background: "#120c10", resizeTo: window, resolution: Math.min(2, window.devicePixelRatio || 1), autoDensity: true }).then(() => {
      // closed before it finished starting: free the WebGL context now, or they pile up and new canvases stay blank
      if (disposed) return app.destroy(true);
      host.current!.appendChild(app.canvas);
      app.canvas.setAttribute("data-testid", "closeup");
      const scene = kind === "playtest" ? playtestCloseup(year, { level }) : kind === "launch" ? launchCloseup(year) : kind === "finale" ? finaleCloseup() : designCloseup(year);
      const root = new Container();
      const bg = new Sprite(freshTex(scene.img));
      // people further back are drawn first, so a crowd overlaps the right way round
      const far = new Container({ sortableChildren: true });
      const near = new Container({ sortableChildren: true });
      root.addChild(bg, far);
      if (kind === "playtest") root.addChild(new Sprite(freshTex(playtestTable(scene, table ? { ...table, year } : { year }))));
      if (display && scene.display) {
        const box = new Sprite(freshTex(display));
        box.position.set(scene.display.x, scene.display.y);
        box.scale.set(scene.display.w / display.w, scene.display.h / display.h);
        root.addChild(box);
      }
      root.addChild(near);
      const fg = new Sprite(freshTex(scene.fg));
      if (quality === "high") fg.filters = [new BlurFilter({ strength: 3, quality: 2 })];
      const maps = lightMaps(CW, CH, scene.lights.map((l) => ({ ...l, strength: l.strength * 0.85 })), scene.shafts, mood);
      const shade = new Sprite(freshTex(maps.shade));
      shade.blendMode = "multiply";
      const glow = new Sprite(freshTex(maps.glow));
      glow.blendMode = "add";
      const em = new Sprite(freshTex(scene.em));
      em.blendMode = "add";
      em.alpha = 0.8;
      const dust = new Dust(110);
      dust.setShafts(scene.shafts.map((s) => s.pts), app.renderer);
      root.addChild(shade, glow, em, dust.layer, fg);
      app.stage.addChild(root);
      const post = new HD2DFilter();
      post.u.uTiltTop = 0.34;
      post.u.uTiltBottom = 0.84;
      // the awards stage is already lit like a show: less bloom, or the winner glows white
      post.u.uBloom = kind === "finale" ? 0.3 : 0.7;
      const layout = () => {
        const { width, height } = app.screen;
        let scale = Math.max(width / CW, height / CH);
        let ox = (width - CW * scale) / 2;
        let oy = (height - CH * scale) / 2;
        // phones, at the table: frame the table and the testers in the open band between the title card and the
        // buttons at the bottom, instead of filling the screen with wall and hiding the table under the readout
        if (width < 761 && kind === "playtest") {
          const top = 190;
          const bottom = height - 74 - Math.min(0.42 * height, 300);
          scale = Math.max(width / 230, (bottom - top) / 150);
          ox = width / 2 - 240 * scale;
          oy = (top + bottom) / 2 - 168 * scale;
        }
        root.scale.set(scale);
        root.position.set(ox, oy);
        app.stage.filterArea = undefined as any;
        root.filters = quality === "high" ? [post] : [];
        if (state.current) Object.assign(state.current, { scale, ox, oy });
        return { scale, ox, oy };
      };
      const l = layout();
      state.current = { app, layers: { far, near }, scene, ...l };
      onResize = () => void layout();
      window.addEventListener("resize", onResize);
      let t = 0;
      app.ticker.add((tk) => {
        t += tk.deltaMS / 1000;
        post.u.uTime = t * 60;
        if (quality === "high") dust.tick(tk.deltaMS / 1000, t);
        draw();
      });
      onReady?.({ scene, toScreen: (x, y) => ({ x: (state.current?.ox ?? 0) + x * (state.current?.scale ?? 1), y: (state.current?.oy ?? 0) + y * (state.current?.scale ?? 1) }) });
      // the smooth zoom in from the studio
      const c = app.canvas;
      if (zoomFrom) {
        c.style.transformOrigin = `${zoomFrom.x}px ${zoomFrom.y}px`;
        c.style.transform = "scale(0.15)";
        c.style.opacity = "0";
        requestAnimationFrame(() => {
          c.style.transition = "transform 420ms cubic-bezier(.2,.7,.2,1), opacity 260ms";
          c.style.transform = "scale(1)";
          c.style.opacity = "1";
        });
      }
    });
    const cache = new Map<string, Sprite>();
    function draw() {
      const st = state.current;
      if (!st) return;
      const seen = new Set<string>();
      for (const a of actorsRef.current) {
        const seat = a.seat >= 0 ? st.scene.seats[a.seat] : st.scene.designer;
        if (!seat) continue;
        const stand = a.stand ?? a.seat < 0;
        const key = `${a.id}`;
        seen.add(key);
        let sp = cache.get(key);
        if (!sp) {
          sp = new Sprite();
          cache.set(key, sp);
        }
        const layer = seat.view === "front" ? st.layers.far : st.layers.near;
        if (sp.parent !== layer) layer.addChild(sp);
        sp.texture = texLazy(hashActor(a, seat.view, stand), () => closeupPerson(a.look, { view: seat.view, stand, armsUp: a.armsUp, mouth: a.mouth }));
        sp.position.set(seat.x, seat.y + a.lean);
        sp.zIndex = seat.y;
      }
      for (const [k, sp] of cache) if (!seen.has(k)) {
        sp.destroy();
        cache.delete(k);
      }
    }
    return () => {
      disposed = true;
      window.removeEventListener("resize", onResize);
      state.current?.app.destroy(true);
      state.current = null;
    };
  }, [kind, year, quality, mood, level, table?.mechanic, table?.theme, table?.format, table?.name]);
  return <div ref={host} style={{ position: "fixed", inset: 0, zIndex: 15 }} />;
}

function hashActor(a: Actor, view: string, stand: boolean): number {
  let h = 5381;
  const s = `${a.look.hair}${a.look.style}${a.look.skin}${a.look.shirt}${a.look.glasses}|${view}|${stand}|${a.armsUp}|${a.mouth}`;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h + 7_000_000;
}

export { look };
